import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const ReactionContext = createContext();

export const useReactions = () => useContext(ReactionContext);

const CACHE_KEY = 'post_reactions_cache';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000;

const cacheManager = {
  get: () => {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_EXPIRY) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  },
  set: (data) => {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  },
};

const FALLBACK_REACTIONS = [
  { name: 'like', emoji: '👍' },
  { name: 'love', emoji: '❤️' },
  { name: 'wow', emoji: '😮' },
  { name: 'happy', emoji: '😄' },
  { name: 'sad', emoji: '😢' },
];

export const ReactionProvider = ({ children }) => {
  const [reactions, setReactions] = useState(cacheManager.get() || FALLBACK_REACTIONS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchReactions = async () => {
      if (cacheManager.get()) return;

      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/user/post/reactions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const parsed = Array.isArray(data) ? data : FALLBACK_REACTIONS;
        setReactions(parsed);
        cacheManager.set(parsed);
      } catch (error) {
        console.error('Failed to fetch reactions:', error);
        setReactions(FALLBACK_REACTIONS);
      } finally {
        setLoading(false);
      }
    };

    fetchReactions();
  }, []);

  const contextValue = useMemo(() => ({ reactions, loading }), [reactions, loading]);

  return (
    <ReactionContext.Provider value={contextValue}>
      {children}
    </ReactionContext.Provider>
  );
}; 
